import noop from 'lodash/noop'
import { action, makeObservable, observable } from 'mobx'
import type { ReactNode } from 'react'

import { getOrCreateContentfulClient } from '@graphql/client'
import type { CustomModalId } from '@graphql/hooks/modal'
import { getModal } from '@graphql/hooks/modal'
import type { Personalizations } from '@lib/hooks/usePersonalizations'

export const DEFAULT_MODAL_CONTENT: ModalContent = {
    title: '',
    primary: '',
    secondary: '',
    body: '',
    hasCloseButton: true,
}

type ModalContent = {
    title: string
    body: ReactNode
    primary: string
    secondary?: string
    size?: ModalSize
    hasCloseButton?: boolean
}

export type ModalSize = 'small' | 'medium'

export type ModalState = {
    id: string
    content: ModalContent
    visible: boolean
    onPrimary: () => void | Promise<void>
    onSecondary: () => void | Promise<void>
    onHide: () => void | Promise<void>
}

class ModalStore {
    id = ''

    content = DEFAULT_MODAL_CONTENT

    visible = false

    onPrimary: () => void | Promise<void> = noop

    onSecondary: () => void | Promise<void> = noop

    onHide: () => void | Promise<void> = noop

    constructor() {
        makeObservable(this, {
            id: observable,
            content: observable.ref,
            visible: observable,
            onPrimary: observable,
            onSecondary: observable,
            onHide: observable,
            reset: action,
            promptById: action,
            prompt: action,
        })
    }

    reset() {
        this.visible = false
        this.content = DEFAULT_MODAL_CONTENT
        this.onPrimary = noop
        this.onSecondary = noop
        this.onHide = noop
    }

    async promptById(
        customId: CustomModalId,
        personalizations: Personalizations,
    ) {
        const { modal } = await getModal(
            getOrCreateContentfulClient(),
            customId,
            personalizations,
        )
        return this.prompt(modal)
    }

    /**
     * Usage:
     *
     * ```ts
     * const boolean = await prompt(config)
     * if (boolean) doThis()
     * else doThat()
     * ```
     */
    async prompt(content: ModalContent) {
        return new Promise<boolean>((resolve) => {
            this.visible = true
            this.content = content

            this.onPrimary = () => {
                this.reset()
                resolve(true)
            }

            this.onSecondary = () => {
                this.reset()
                resolve(false)
            }

            this.onHide = () => {
                this.reset()
                resolve(false)
            }
        })
    }
}

export const modalStore = new ModalStore()
